import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import {
    fetchPages,
    fetchTemplates,
    updatePage,
    selectAllPages,
    selectTemplates,
    selectPageStatus,
    selectPageSaving,
    selectPageError,
} from './PageSlice';

// What each `kind` means, shown as a hint in the grid so it is obvious why some
// pages have no article type.
const KIND_LABEL = {
    type: 'Listing (article type)',
    home: 'Home (all types)',
    static: 'Static page',
    search: 'Search results',
};

// Site defaults, mirroring TEMPLATE_SLOTS in the renderer — shown as the
// "inherit" option so it is clear what an empty slot actually renders with.
// `group` is the src/templates/ folder the slot's options come from; each
// dropdown lists only that folder, so a card can't be picked as a page body.
const TEMPLATE_SLOTS = {
    template: { default: 'main', group: 'pages' },
    cardTemplate: { default: 'card', group: 'cards' },
    articleTemplate: { default: 'article', group: 'articles' },
};

// A template picker. An empty value means "use the site default" (stored NULL).
// `InputLabelProps.shrink` is required, not cosmetic: the default value is '',
// so MUI treats the field as empty and parks the label over the input — but a
// native <select> always paints its selected option, and the two overlap.
const TemplateSelect = ({ label, field, value, templates, helperText, onChange }) => {
    const { default: fallback, group } = TEMPLATE_SLOTS[field];
    const options = (templates && templates[group]) || [];

    return (
        <TextField
            select
            label={label}
            value={value}
            onChange={onChange}
            helperText={`${helperText} — from src/templates/${group}/`}
            fullWidth
            size='small'
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
        >
            <option value=''>— default ({fallback}) —</option>
            {options.map((name) => (
                <option key={name} value={name}>
                    {name}
                </option>
            ))}
        </TextField>
    );
};

// #region EDIT DIALOG
const PageEditDialog = ({ page, open, saving, templates, onClose, onSave }) => {
    const [form, setForm] = useState(null);

    // Reload the form whenever a different page is opened.
    useEffect(() => {
        if (!page) return setForm(null);
        setForm({
            headTitle: page.headTitle || '',
            headDescription: page.headDescription || '',
            headerTitle: page.headerTitle || '',
            navLabel: page.navLabel || '',
            template: page.template || '',
            cardTemplate: page.cardTemplate || '',
            articleTemplate: page.articleTemplate || '',
            isVisible: !!page.isVisible,
        });
    }, [page]);

    if (!page || !form) return null;

    const setField = (field) => (event) => {
        const value = field === 'isVisible' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // Send only what actually changed, so an untouched field is never written.
    const handleSave = () => {
        const changes = {};
        if (form.headTitle !== (page.headTitle || '')) changes.headTitle = form.headTitle;
        if (form.headDescription !== (page.headDescription || '')) changes.headDescription = form.headDescription;
        if (form.headerTitle !== (page.headerTitle || '')) changes.headerTitle = form.headerTitle;
        if (form.navLabel !== (page.navLabel || '')) changes.navLabel = form.navLabel;
        if (form.template !== (page.template || '')) changes.template = form.template;
        if (form.cardTemplate !== (page.cardTemplate || '')) changes.cardTemplate = form.cardTemplate;
        if (form.articleTemplate !== (page.articleTemplate || '')) changes.articleTemplate = form.articleTemplate;
        if (form.isVisible !== !!page.isVisible) changes.isVisible = form.isVisible;

        if (!Object.keys(changes).length) return onClose();
        onSave(page.idPage, changes);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                {page.filename}
                <div style={{ fontSize: 12, color: '#666', fontWeight: 400 }}>{KIND_LABEL[page.kind] || page.kind}</div>
            </DialogTitle>
            <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                    <TextField
                        label='Head title'
                        helperText='Browser tab and search-result title (<title>)'
                        value={form.headTitle}
                        onChange={setField('headTitle')}
                        fullWidth
                        size='small'
                    />
                    <TextField
                        label='Meta description'
                        helperText='The <meta name="description"> snippet shown by search engines'
                        value={form.headDescription}
                        onChange={setField('headDescription')}
                        fullWidth
                        multiline
                        minRows={3}
                        size='small'
                    />
                    <TextField
                        label='Header title'
                        helperText='Site brand shown in the page header'
                        value={form.headerTitle}
                        onChange={setField('headerTitle')}
                        fullWidth
                        size='small'
                    />
                    <TextField
                        label='Nav label'
                        helperText={
                            page.kind === 'type'
                                ? `Leave empty to use the article type's display name ("${page.displayName || ''}")`
                                : 'Text of this page’s link in the top nav'
                        }
                        value={form.navLabel}
                        onChange={setField('navLabel')}
                        fullWidth
                        size='small'
                    />
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                            Templates — each slot lists the files in its own <code>src/templates/</code> folder. Leave a slot on “default” to
                            inherit the site-wide one.
                        </div>
                        <TemplateSelect
                            label='Page template'
                            field='template'
                            value={form.template}
                            templates={templates}
                            helperText='Renders this page’s body'
                            onChange={setField('template')}
                        />
                        {/* Cards and article pages only exist for feeds. */}
                        {(page.kind === 'type' || page.kind === 'home') && (
                            <TemplateSelect
                                label='Card template'
                                field='cardTemplate'
                                value={form.cardTemplate}
                                templates={templates}
                                helperText='One item in the listing — also used wherever this type appears (home feed, search)'
                                onChange={setField('cardTemplate')}
                            />
                        )}
                        {page.kind === 'type' && (
                            <TemplateSelect
                                label='Article template'
                                field='articleTemplate'
                                value={form.articleTemplate}
                                templates={templates}
                                helperText='Detail page for an article of this type'
                                onChange={setField('articleTemplate')}
                            />
                        )}
                    </div>
                    <FormControlLabel
                        control={<Switch checked={form.isVisible} onChange={setField('isVisible')} />}
                        label='Visible (rendered to the site and linked in the nav)'
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant='contained' disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// #region PAGES
const Pages = () => {
    const dispatch = useDispatch();

    const pages = useSelector(selectAllPages);
    const templates = useSelector(selectTemplates);
    const status = useSelector(selectPageStatus);
    const saving = useSelector(selectPageSaving);
    const error = useSelector(selectPageError);

    const [editing, setEditing] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(fetchPages());
        dispatch(fetchTemplates());
    }, [dispatch]);

    useEffect(() => {
        if (error) setSnackbar({ open: true, message: error, severity: 'error' });
    }, [error]);

    const handleSave = useCallback(
        async (id, changes) => {
            const result = await dispatch(updatePage({ id, changes }));
            if (updatePage.fulfilled.match(result)) {
                setEditing(null);
                // Visibility and template changes reach other pages (the nav and
                // this type's cards on the home feed), so those rebuild everything.
                const siteWide = ['isVisible', 'template', 'cardTemplate', 'articleTemplate'].some((f) => changes[f] !== undefined);
                setSnackbar({
                    open: true,
                    message: siteWide ? 'Page saved — whole site re-rendered' : 'Page saved and re-rendered',
                    severity: 'success',
                });
            }
        },
        [dispatch]
    );

    const rows = useMemo(
        () =>
            (pages || []).map((p) => ({
                ...p,
                id: p.idPage,
                kindLabel: KIND_LABEL[p.kind] || p.kind,
            })),
        [pages]
    );

    const columns = [
        { field: 'filename', headerName: 'File', width: 170 },
        { field: 'kindLabel', headerName: 'Kind', width: 165 },
        {
            field: 'navLabelShown',
            headerName: 'Nav',
            width: 150,
            // DataGrid v6 signature: a single `params` object.
            valueGetter: (params) => (params.row.showInNav ? params.row.navLabel || params.row.displayName || '' : '—'),
        },
        {
            field: 'templates',
            headerName: 'Templates',
            width: 150,
            sortable: false,
            // Only the overrides; an inherited slot shows as the default name in grey.
            valueGetter: (params) =>
                [params.row.template || 'main', params.row.cardTemplate || 'card', params.row.articleTemplate || 'article'].join(' / '),
            renderCell: (params) => (
                <Tooltip title='page / card / article'>
                    <span style={{ fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value}</span>
                </Tooltip>
            ),
        },
        { field: 'headTitle', headerName: 'Head title', flex: 1, minWidth: 180 },
        {
            field: 'headDescription',
            headerName: 'Description',
            flex: 1.4,
            minWidth: 220,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'isVisible',
            headerName: 'Visible',
            width: 90,
            renderCell: (params) =>
                params.value ? (
                    <Chip label='LIVE' size='small' color='success' variant='outlined' style={{ fontSize: '0.7rem', height: '20px' }} />
                ) : (
                    <Chip label='HIDDEN' size='small' color='default' variant='outlined' style={{ fontSize: '0.7rem', height: '20px' }} />
                ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <IconButton color='primary' onClick={() => setEditing(params.row)}>
                    <EditIcon />
                </IconButton>
            ),
        },
    ];

    // #region RETURN
    return (
        <div
            style={{
                height: 'calc(100vh - 300px)',
                minHeight: '300px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '8px',
                overflow: 'hidden',
            }}
        >
            <div style={{ fontSize: 12, color: '#666', flexShrink: 0 }}>
                Head/meta text for each public page. Saving re-renders the affected HTML immediately — no restart needed. Filename and kind are
                structural and set by migration.
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={status === 'loading'}
                    disableRowSelectionOnClick
                    onRowDoubleClick={(params) => setEditing(params.row)}
                    getRowId={(r) => r.id}
                    hideFooter
                />
            </div>

            <PageEditDialog
                page={editing}
                open={!!editing}
                saving={saving}
                templates={templates}
                onClose={() => setEditing(null)}
                onSave={handleSave}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Pages;
